const getEnvVar = (key: 'VITE_BOOKING_API_URL' | 'VITE_CONTENT_API_URL') => {
  const metaEnv = typeof import.meta !== 'undefined' ? (import.meta.env as Record<string, string | undefined>) : undefined;
  if (metaEnv && metaEnv[key]) {
    return metaEnv[key];
  }

  type ProcessLike = { env?: Record<string, string | undefined> };
  const nodeEnv =
    typeof globalThis !== 'undefined' && 'process' in globalThis
      ? ((globalThis.process as ProcessLike | undefined)?.env)
      : undefined;
  return nodeEnv?.[key];
};

const isLocalHostname = (hostname: string) =>
  hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';

const isNexcaveHostname = (hostname: string) =>
  hostname === 'nexcave.com' || hostname.endsWith('.nexcave.com');

const isDefaultWebPort = (protocol: string, port: string) =>
  !port || (protocol === 'https:' && port === '443') || (protocol === 'http:' && port === '80');

const isLoopbackApiUrl = (url: string) =>
  /^https?:\/\/(localhost|127\.0\.0\.1|::1)(:\d+)?/i.test(url);

// API Configuration for different environments
const getApiUrl = (service: 'booking' | 'content') => {
  const envUrl =
    service === 'booking'
      ? getEnvVar('VITE_BOOKING_API_URL')
      : getEnvVar('VITE_CONTENT_API_URL');

  // In production, use relative paths that will be proxied by nginx
  // In development, use localhost
  if (typeof window !== 'undefined') {
    // Local dev should always go through the Vite proxy to avoid CORS/port confusion,
    // even if accessed via a custom hostname (e.g. hosts file mapping).
    if (import.meta.env.DEV) {
      return service === 'booking' ? '/api/booking' : '/api';
    }

    const hostname = window.location.hostname;
    const isLocalHost = isLocalHostname(hostname);

    if (envUrl) {
      if (!(import.meta.env.PROD && !isLocalHost && isLoopbackApiUrl(envUrl))) {
        return envUrl;
      }
    }

    if (isLocalHost) {
      return service === 'booking' ? 'http://localhost:3003/api' : 'http://localhost:3004/api';
    }

    if (!isNexcaveHostname(hostname) || !isDefaultWebPort(window.location.protocol, window.location.port)) {
      return service === 'booking' ? 'http://localhost:3003/api' : 'http://localhost:3004/api';
    }

    return service === 'booking' ? '/booking-api/api' : '/content-api/api';
  }

  // Server-side rendering or build time
  if (envUrl) {
    return envUrl;
  }

  if (import.meta.env.PROD) {
    return service === 'booking' ? '/booking-api/api' : '/content-api/api';
  }

  return service === 'booking' ? 'http://localhost:3003/api' : 'http://localhost:3004/api';
};

// Helper function to get full API URL - must be dynamic!
export const getContentApiUrl = (endpoint: string) => {
  const baseUrl = getApiUrl('content');
  return `${baseUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
};

export const getBookingApiUrl = (endpoint: string) => {
  const baseUrl = getApiUrl('booking');
  return `${baseUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
};
